import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { DataList, NotificationFeed, NotificationPreferences, StatusPill, describeUserAgent, type DataListColumn } from "@ozpanel/kit";
import { Button, Section, Technical, formatRelative, useConfirm, useToast } from "@ozpanel/ui";
import { useMailboxApi, type PushSubscription } from "../api";
import { InstallAndNotify } from "./InstallAndNotify";

// Notifications, in the order a person sets them up: get alerts working on this device first, then
// see which devices receive them, then choose what you are told about, and finally the notices
// themselves. The browser half (permission, worker, VAPID key) is the shared @ozpanel/kit/webpush.
export function Notifications() {
  const api = useMailboxApi();
  const qc = useQueryClient();
  const { notify } = useToast();
  const confirm = useConfirm();
  // Arrived from scanning a phone-setup code (PairScreen): said once, at the top of the setup it continues.
  const justPaired = new URLSearchParams(useLocation().search).get("setup") === "1";

  const subs = useQuery({ queryKey: ["push-subs"], queryFn: api.pushSubscriptions, retry: false });
  const list = subs.data?.subscriptions ?? [];
  // The server's list carries no endpoint, so "this device" is the row whose browser string is THIS
  // browser's — said only when exactly one row matches, never guessed between two.
  const mine = list.filter((s) => s.user_agent === navigator.userAgent);
  const thisId = mine.length === 1 ? mine[0]?.id : undefined;

  const remove = async (s: PushSubscription) => {
    try {
      await api.unsubscribePush(s.id);
      void qc.invalidateQueries({ queryKey: ["push-subs"] });
      notify({ kind: "success", title: "Device removed" });
    } catch (e) {
      notify({ kind: "error", title: "Could not remove it", body: e instanceof Error ? e.message : String(e) });
    }
  };

  const cols: DataListColumn<PushSubscription>[] = [
    {
      key: "user_agent",
      label: "Device",
      phone: "title",
      value: (s) => describeUserAgent(s.user_agent),
      render: (s) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          {describeUserAgent(s.user_agent)}
          {s.id === thisId && <StatusPill tone="accent">this device</StatusPill>}
          <Technical>
            <span className="block w-full font-mono text-[11px] break-all text-faint">{s.user_agent}</span>
          </Technical>
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Added",
      sortable: true,
      value: (s) => s.created_at || "",
      render: (s) => (s.created_at ? formatRelative(s.created_at) : "—"),
    },
  ];

  return (
    <div className="space-y-4">
      {confirm.dialog}
      {justPaired && (
        <div className="rounded-lg border border-line bg-bg2 px-4 py-3 text-[13.5px]" role="status">
          <strong>This phone is signed in.</strong> <span className="text-muted">Now add the app to the home screen and turn on new-mail alerts — it stays signed in.</span>
        </div>
      )}
      <InstallAndNotify />

      <Section title="Devices that get alerts" hint={subs.data ? `${list.length}` : undefined}>
        <DataList
          items={list}
          columns={cols}
          getRowKey={(s) => String(s.id)}
          loading={subs.isLoading}
          error={subs.error}
          errorContext="your alert devices"
          emptyMessage="No device gets alerts yet."
          emptyHint="Turning alerts on above adds the device you are using now."
          rowActions={(s) => (
            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                confirm.ask(
                  {
                    title: `Stop alerts on ${describeUserAgent(s.user_agent)}?`,
                    body: "That device stops getting new-mail alerts. It can be turned on again from that device.",
                    confirmLabel: "Remove",
                  },
                  () => remove(s),
                )
              }
            >
              Remove
            </Button>
          )}
        />
      </Section>

      {/* WHERE you are told, beside WHETHER you are subscribed. */}
      <NotificationPreferences />

      {/* The notices themselves — sending-limit, forward and bounce notices — the same feed the
          account console shows, so both audiences read their notices the same way. */}
      <NotificationFeed />
    </div>
  );
}
